import { useParams, Outlet } from 'react-router';
import { Suspense, useRef, useState, useLayoutEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../redux/hooks';
import { toggleCart } from '../redux/slices/cartSlice';
import RestaurantGate from '../components/RestaurantGate';
import Cart from '../components/Cart';
import { useNavWidget } from '../hooks/useNavWidget';
import { useFooterWidget } from '../hooks/useFooterWidget';
import { useNavigation } from '../hooks/useNavigation';
import DynamicPage from '../components/DynamicPage';

export default function RestaurantIdGuard() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { isEditControl } = useAppSelector((state) => state.editControl);

  const { handleNavClick, handleLogoClick } = useNavigation();
  const nav = useNavWidget();
  const footer = useFooterWidget();

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(72);

  const isValidUUID = useMemo(() => {
    if (!id) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  }, [id]);

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) resizeObserver.observe(headerRef.current);

    return () => resizeObserver.disconnect();
  }, [nav.HeaderComponent]);

  const handleCartClick = useCallback(() => {
    dispatch(toggleCart());
  }, [dispatch]);

  if (id && !isValidUUID) {
    return (
      <div>
        {isEditControl && !nav.isNavEditMode && (
          <button
            onClick={nav.actions.toggleEditMode}
            disabled={nav.loading.updateLoading || nav.loading.fetchLoading}
            className="fixed left-1/2 transform -translate-x-1/2 z-60 px-4 py-2 rounded-full transition-colors flex items-center space-x-2 shadow-lg"
            style={{ 
              backgroundColor: '#EE4445', 
              color: 'white',
              top: `${headerHeight + 18}px` 
            }}
            title="Edit navigation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            <span className="text-sm font-medium">Edit</span>
          </button>
        )}

        {/* Added id="main-header" */}
        <header id="main-header" ref={headerRef} className="fixed top-0 left-0 right-0 z-50">
          {nav.HeaderComponent && (
            <Suspense fallback={<div className="h-16 shadow-md animate-pulse bg-transparent" />}>
              <nav.HeaderComponent
                data={nav.headerData}
                onNavClick={handleNavClick}
                onLogoClick={handleLogoClick}
                onCartClick={handleCartClick}
                onUpdate={nav.actions.update}
                onSave={nav.actions.save}
                isEditing={nav.isNavEditMode}
              />
            </Suspense>
          )}
        </header>

        <main className="min-h-screen">
          <RestaurantGate requiresRestaurant={true} redirectTo="/select-location">
            <DynamicPage pageType={id} />
          </RestaurantGate>

          <footer className="w-full relative">
            {footer.FooterComponent && (
              <Suspense fallback={<div className="h-32 bg-gray-800 animate-pulse" />}>
                <footer.FooterComponent
                  data={footer.footerData}
                  navItems={footer.navItems}
                  onUpdate={footer.actions.update}
                  isEditing={footer.isEditMode}
                />
              </Suspense>
            )}
          </footer>
        </main>

        <Cart />
      </div>
    );
  }

  return <Outlet />;
}